import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

const TermsConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="ghost" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver al inicio
            </Button>
          </Link>
          <div className="flex items-center mb-4">
            <FileText className="h-8 w-8 text-cyan-500 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">Términos y Condiciones</h1>
          </div>
          <p className="text-gray-600">Última actualización: Enero 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-md p-8 space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Aceptación de los Términos</h2>
            <p className="text-gray-700 leading-relaxed">
              Al acceder y utilizar TuClinicApp, usted acepta estar sujeto a estos Términos y Condiciones de uso. 
              Si no está de acuerdo con alguno de estos términos, le recomendamos no utilizar nuestro servicio.
              Estos términos aplican a todos los visitantes, usuarios y otras personas que accedan o utilicen el servicio.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Descripción del Servicio</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              TuClinicApp es una plataforma de directorio de clínicas dentales que permite a los usuarios:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Buscar y encontrar clínicas dentales en Bolivia, Paraguay y El Salvador</li>
              <li>Ver información detallada de las clínicas incluyendo servicios, ubicación y horarios</li>
              <li>Contactar directamente con las clínicas a través de llamadas o WhatsApp</li>
              <li>Registrar nuevas clínicas en el directorio</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Uso del Servicio</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Al utilizar nuestro servicio, usted se compromete a:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Proporcionar información veraz y actualizada</li>
              <li>No utilizar el servicio para fines ilegales o no autorizados</li>
              <li>No interferir con el funcionamiento normal de la plataforma</li>
              <li>Respetar los derechos de propiedad intelectual</li>
              <li>No publicar contenido falso, engañoso o difamatorio</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Registro de Clínicas</h2>
            <p className="text-gray-700 leading-relaxed">
              Las clínicas que deseen registrarse en nuestro directorio deben proporcionar información precisa y 
              actualizada. Nos reservamos el derecho de verificar y aprobar las solicitudes de registro. 
              Las clínicas son responsables de mantener su información actualizada y de responder a las 
              consultas de los usuarios de manera oportuna y profesional.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Limitación de Responsabilidad</h2>
            <p className="text-gray-700 leading-relaxed">
              TuClinicApp actúa únicamente como intermediario entre los usuarios y las clínicas dentales. 
              No somos responsables de la calidad de los servicios prestados por las clínicas, ni de las 
              decisiones tomadas por los usuarios basadas en la información proporcionada en nuestra plataforma.
              Recomendamos a los usuarios verificar directamente con las clínicas cualquier información 
              antes de tomar decisiones médicas.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Privacidad y Datos Personales</h2>
            <p className="text-gray-700 leading-relaxed">
              Respetamos su privacidad y protegemos sus datos personales de acuerdo con las leyes aplicables.
              La información recopilada se utiliza únicamente para mejorar nuestros servicios y facilitar 
              la comunicación entre usuarios y clínicas. No compartimos información personal con terceros 
              sin su consentimiento expreso, excepto cuando sea requerido por ley.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Propiedad Intelectual</h2>
            <p className="text-gray-700 leading-relaxed">
              Todo el contenido presente en TuClinicApp, incluyendo pero no limitado a textos, gráficos, 
              logotipos, iconos, imágenes y software, es propiedad de TuClinicApp o de sus proveedores de 
              contenido y está protegido por las leyes de propiedad intelectual aplicables.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Modificaciones</h2>
            <p className="text-gray-700 leading-relaxed">
              Nos reservamos el derecho de modificar estos Términos y Condiciones en cualquier momento. 
              Los cambios entrarán en vigor inmediatamente después de su publicación en la plataforma. 
              El uso continuado del servicio después de dichas modificaciones constituye su aceptación 
              de los nuevos términos.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contacto</h2>
            <p className="text-gray-700 leading-relaxed">
              Si tiene alguna pregunta sobre estos Términos y Condiciones, puede contactarnos a través 
              de los canales de comunicación disponibles en nuestra plataforma.
            </p>
          </section>

          {/* Footer note */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-sm text-gray-500 text-center">
              Al utilizar TuClinicApp, usted reconoce haber leído, entendido y aceptado estos Términos y Condiciones.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
